import { createRouter, createWebHistory } from 'vue-router';
import _ from 'lodash';
import type { RouteLocationNormalized } from 'vue-router';
import { setCurrentMfeRoute, redirectToNotFound } from 'host/mfeBridge';
import { createRoutes } from '@Router/routes';
import { getBasePath } from '@Src/mfe.config';
import { useEsignatureFlowStore } from '@Src/stores/esignatureFlow.store';
import { useSigningRequestStore } from '@Src/stores/signingRequest.store';
import { getRecipientAndDocumentStatusByRecipientTokenApi } from '@Api/esignature.api';
import { getRecipientTokenByActivitySessionApi } from '@Api/activitySession.api';
import { resolveRecipientStatus } from '@Enums/recipientStatusType.enum';
import { getRequestStatus } from '@Types/documentSigningStatus.type';
import { getSigningRequestRedirect } from '@Router/signingRequestRedirect';


const basePath = getBasePath() ?? '/';
const router = createRouter({
    history: createWebHistory(),
    routes: createRoutes(basePath)
});


router.beforeEach(async (to, from, next) => {
    console.log("[Esign MFE Router] Navigating to: ", to.name?.toString() + " from " + from.name?.toString());
    setCurrentMfeRoute(to);


    const requiresFlow = Boolean(to.meta?.requiresFlow);
    const requiresSigningValidation = Boolean(to.meta?.requiresSigningValidation);

    // Check signing request status before allowing access to signing routes
    if (requiresSigningValidation) {
        const redirect = await validateSigningRequest(to);
        if (redirect) {
            next(redirect);
            return;
        }
    }

    if (requiresFlow) {
        await prepareEsignatureFlow();


        setCurrentFlowRoute(to);
    }

    next();
});


// Validate signing request status before allowing access
async function validateSigningRequest(to: RouteLocationNormalized): Promise<{ name: string } | null> {
    const signingRequestStore = useSigningRequestStore();

    if (!signingRequestStore.validatedToken) {
        const recipientToken = await getRecipientTokenByActivitySessionApi();
        console.log("[ReviewDocumentView] Retrieved recipientToken from activity session: ", recipientToken);

        if (!recipientToken) {
            console.log("NO VALIDATED TOKEN");
            redirectToNotFound();
            return null;
        }

        await signingRequestStore.setValidatedToken(recipientToken);
        console.log("[ReviewDocumentView] Token saved from activity session");
    }

    // Perform status check for routes after Screen 2
    const token = signingRequestStore.validatedToken;
    if (!token) {
        redirectToNotFound();
        return null;
    }
    const status = await getRecipientAndDocumentStatusByRecipientTokenApi(token);
    const docStatus = getRequestStatus(status);
    if (docStatus === undefined) {
        redirectToNotFound();
        return null;
    }
    const recipientStatus = resolveRecipientStatus(status.recipientStatusType);

    const redirectName = getSigningRequestRedirect(docStatus, recipientStatus);

    if (redirectName === 'not-found') {
        redirectToNotFound();
        return null;
    }
    if (redirectName !== null) {
        console.log(`[Router Guard] Redirecting to ${redirectName}`);
        if (to.name === redirectName) {
            console.log(`[Router Guard] Already navigating to ${redirectName}, allowing access`);
            return null;
        }
        return { name: redirectName };
    }

    console.log('[Router Guard] Signing request is active, allowing access');
    return null;
}

// ensure client flows are loaded
async function prepareEsignatureFlow(): Promise<void> {
    const esignatureFlowStore = useEsignatureFlowStore();
    const isFlowEmpty = _.isEmpty(esignatureFlowStore.flowSteps);

    if (isFlowEmpty) {
        await esignatureFlowStore.loadEsignatureFlow();
    }
}



function setCurrentFlowRoute(to: RouteLocationNormalized): void {
    const esignatureFlowStore = useEsignatureFlowStore();
    esignatureFlowStore.setCurrentRoute(to.name as string);
}


export default router;